<script>
import {defineComponent, reactive} from "vue";
import Icon from "../Icon.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import ResourceDisplay from "../ResourceDisplay.vue";

export default defineComponent({
  name: "PageHeader",
  components: {ResourceDisplay, Icon},
  props: {
    logoResource: {
      type: Object,
      required: false
    },
    /**
     * The alt attribute for the logo image.
     */
    logoAlt: {
      type: String,
      default: ''
    },
    /**
     * Show/ hide the logo. If off, organization name will be displayed
     */
    showLogo: {
      type: Boolean,
      default: true
    },
    /**
     * The max height of the logo.
     * Any valid css size syntax.
     * When user customization is implemented, 100px should be the max allowable size.
     */
    logoMaxHeight: {
      type: String,
      default: '60px'
    },
    /**
     * The name of the organization.
     * Will be used in place of the logo if none exists on the org.
     */
    orgName: {
      type: String,
      required: true
    },
    /**
     * Background color of header.
     * Can be any valid css color syntax.
     */
    backgroundColor: {
      type: String,
      default: '#FFFFFF'
    },
    /**
     * Text color in header.
     * Can be any valid css color syntax.
     */
    textColor: {
      type: String,
      default: '#000000'
    },

    /**
     * Will hide the donate button if true.
     */
    hideDonateButton: {
      type: Boolean,
      default: false
    },
    /**
     * Color of the donate button to be used as outline and text color.
     */
    donateButtonBackgroundColor: {
      type: String,
      default: '#104205FF'
    },
    /**
     * Background color of donate button to be used on hover.
     */
    donateButtonHoverBackgroundColor: {
      type: String,
      default: '#082103FF'
    },
    /**
     * Text color of donate button to be used on hover.
     */
    donateButtonTextColor: {
      type: String,
      default: '#FFFFFF'
    },
  },
  emits: ['donate-click', 'logo-click'],
  setup(props) {
    props = reactive(props);
    const analyticEventStore = useAnalyticEventStore();
    return {
      classes: {
        'header-content-container': true,
        [`logo-position--${props.logoPosition || 'center'}`]: true,
      },
      analyticEventStore
    };
  },
  data() {
    return {};
  },
  computed: {
    cssProps() {
      return {
        '--background-color': this.backgroundColor,
        '--text-color': this.textColor,
        '--logo-max-height': this.logoMaxHeight,
        '--button-color': this.donateButtonBackgroundColor,
        '--button-hover': this.donateButtonHoverBackgroundColor,
        '--button-text': this.donateButtonTextColor,
      };
    },
  },
  watch: {},
  mounted() {
  },
  methods: {
    logNavigation() {
      this.analyticEventStore.logEvent({
        action: `landing-header-navigate-back`,
      });
      this.$router.push({name: 'landing'});
    }
  }
});

</script>

<template>
  <div
    id="landing-page-header"
    :key="showLogo.toString()"
    class="outer-container "
    :style="cssProps"
  >
    <div class="inner-container">
      <div
        :class="classes"
        class="test-cont"
      >
        <div
          class="logo-link"
          @click="$emit('logo-click')"
        >
          <template
            v-if="logoResource && showLogo"
            class="logo-container"
          >
            <resource-display
              :resource-id="logoResource?.id"
              class="logo-image"
            />
          </template>
          <template v-else-if="!showLogo">
            <p
              class="organization-name-header"
            >
              {{ orgName }}
            </p>
          </template>
        </div>
        <slot name="right-icon-slot" />
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/styles/_variables.scss';


#landing-page-header {
  background-color: var(--background-color);
  color: var(--text-color);
  text-align: left;
  min-height: 80px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  z-index: 10;

  .inner-container {
    padding: 0 $space-md !important;

    .header-content-container {
      .organization-name-header {
        @apply text-xl font-bold;

        @screen sm {
          @apply text-2xl;
        }
      }

      .logo-image :deep(img){
        max-height: var(--logo-max-height);
        min-height: var(--logo-max-height);
        min-width: 0;
      }

      .logo-link {
        margin: 0;
        cursor: pointer;
      }

      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: $space-md;

      @screen sm {
        gap: $space-sm;
      }


    }
  }
}
</style>
